import { Box, Typography, Modal, IconButton, Paper } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Close } from '@mui/icons-material'
import { ECGBackground } from './ECGBackground'
import { sampleECGData } from '../utils/sampleECGData'
import { ECGChart } from './ECGChart'
import { calculateAverageWaveform } from '../utils/calculateAverageWaveform'
import { useECGAnnotations } from '../context/ECGAnnotationsContext'
import { useState, useEffect } from 'react'
import { TranscriptionInput } from 'design-system/input'
import { ECGTechnicalInfo } from './ECGTechnicalInfo'

interface ECGModalProps {
  open: boolean
  onClose: () => void
  leadName: string
  data: number[]
  highlight?: boolean
  avgWave?: Record<string, number>
  bpm?: number
}

/**
 * Modal para visualização ampliada de uma derivação de ECG
 * Exibe detalhes do status e gráfico em tamanho maior
 */
export const ECGModal = ({
  open,
  onClose,
  leadName,
  data,
  highlight,
  avgWave,
  bpm
}: ECGModalProps) => {
  const theme = useTheme()
  // always use 10s for modal view
  const durationSeconds = 10
  const chartData = sampleECGData(data, durationSeconds)
  // ECG paper standard: 25mm/s speed
  const maxTimeInMm = durationSeconds * 25

  // Converter avgWave para formato do gráfico
  const convertAvgWaveToChartData = (
    avgWaveData: Record<string, number> | undefined
  ): Array<{ time: number; voltage: number }> => {
    if (!avgWaveData) return []

    // Extrair e ordenar as colunas numericamente
    const colKeys = Object.keys(avgWaveData)
      .filter(key => key.startsWith('col'))
      .sort((a, b) => {
        const numA = Number.parseInt(a.replaceAll('col', ''), 10)
        const numB = Number.parseInt(b.replaceAll('col', ''), 10)
        return numA - numB
      })

    // Converter para formato { time, voltage }
    // Assumindo 500Hz: cada ponto = 1/500s = 0.002s = 0.05mm (a 25mm/s)
    const samplingRate = 500 // Hz
    const mmPerSecond = 25 // Standard ECG paper speed

    return colKeys.map((key, index) => ({
      time: (index / samplingRate) * mmPerSecond,
      voltage: avgWaveData[key]
    }))
  }

  // Usar avgWave se disponível, caso contrário calcular
  const averageData = avgWave
    ? convertAvgWaveToChartData(avgWave)
    : calculateAverageWaveform(chartData, 1)

  // Calcular duração baseada nos dados reais
  const averageDurationSeconds = 1
  const averageMaxTimeInMm = averageDurationSeconds * 25

  const { getAnnotation, addAnnotation } = useECGAnnotations()
  const [annotation, setAnnotation] = useState('')

  // Carrega a anotação existente quando o modal abre
  useEffect(() => {
    if (open) {
      setAnnotation(getAnnotation(leadName))
    }
  }, [open, leadName, getAnnotation])

  const handleClose = () => {
    addAnnotation(leadName, annotation)
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Paper
        sx={{
          width: 'auto',
          maxWidth: '95vw',
          height: { xs: '95%', sm: '80%' },
          maxHeight: '900px',
          minHeight: { xs: '500px', sm: '540px' },
          overflowY: 'auto',
          p: 3,
          borderRadius: 2,
          outline: 'none',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header do Modal */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Typography
            variant='h5'
            // se o font size for responsivo o texto é maior, senao mantem
            sx={{
              fontWeight: 'bold',
              color: theme.palette.primary.main,
              fontSize: { xs: '16px', sm: 'auto' }
            }}
          >
            Derivação {leadName} - Visualização Ampliada
          </Typography>
          <IconButton onClick={handleClose} size='large'>
            <Close />
          </IconButton>
        </Box>

        {/* Status da Derivação */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant='subtitle1'
            sx={{ fontWeight: 'medium', fontSize: { xs: '14px', sm: 'auto' } }}
          >
            Ánalise:{' '}
            <span
              style={{
                color: highlight
                  ? theme.palette.ecgAlert.main
                  : theme.palette.ecgNormal.main,
                marginLeft: '8px',
                fontWeight: 'bold'
              }}
            >
              {highlight
                ? 'Compatível com alteração'
                : 'Sem alteração detectada'}
            </span>
          </Typography>
        </Box>

        {/* Gráficos */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flex: 1,
            mb: 2,
            minHeight: 0,
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          {/* Gráfico Principal (Lead Completo) */}
          <Box
            sx={{
              width: `${maxTimeInMm * 5}px`,
              maxWidth: '100%',
              height: '200px',
              border: `2px solid ${highlight ? theme.palette.ecgAlert.main : theme.palette.ecgNormal.main}`,
              borderRadius: 1,
              position: 'relative',
              overflowX: 'auto',
              overflowY: 'hidden',
              // Custom scrollbar (Webkit, Firefox)
              '&::-webkit-scrollbar': {
                height: '6px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#aaa',
                borderRadius: '3px'
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f5f5f5'
              },
              scrollbarHeight: 'thin' // Firefox
            }}
          >
            <ECGBackground maxTimeInMm={maxTimeInMm} />
            <ECGChart
              data={chartData}
              maxTimeInMm={maxTimeInMm}
              strokeWidth={0.5}
              heightInLargeSquares={8}
            />
          </Box>

          {/* Gráfico de Média (1s) */}
          <Box
            sx={{
              width: `${averageMaxTimeInMm * 5.2}px`, // 1s = 25mm = 125px
              minWidth: `${averageMaxTimeInMm * 5.2}px`,
              height: '200px',
              borderRadius: 1,
              position: 'relative',
              overflowX: 'auto',
              overflowY: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              border: `2px solid ${highlight ? theme.palette.ecgAlert.main : theme.palette.ecgNormal.main}`,
              '&::-webkit-scrollbar': {
                height: '6px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#aaa',
                borderRadius: '3px'
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f5f5f5'
              },
              scrollbarHeight: 'thin' // Firefox
            }}
          >
            <Typography
              variant='caption'
              sx={{
                position: 'absolute',
                top: 4,
                left: 4,
                right: 4,
                fontWeight: 'bold',
                zIndex: 2,
                color: theme.palette.primary.main,
                bgcolor: `${theme.palette.background.paper}CC`,
                px: 1,
                borderRadius: 1,
                lineHeight: { xs: '1', sm: '1.66' }
              }}
            >
              Complexo Médio
            </Typography>
            <ECGBackground maxTimeInMm={averageMaxTimeInMm} />
            <ECGChart
              data={averageData}
              maxTimeInMm={averageMaxTimeInMm}
              strokeWidth={0.5}
              margin={{ left: 20, right: 20, top: 0, bottom: 0 }}
              heightInLargeSquares={8}
            />
          </Box>
        </Box>

        {/* Legenda Técnica ECG */}
        <ECGTechnicalInfo durationSeconds={durationSeconds} bpm={bpm} />

        {/* Campo de Anotações Médicas */}
        <Box sx={{ mt: 2 }}>
          <TranscriptionInput
            label={`Anotações Médicas - Derivação ${leadName}`}
            placeholder='Digite suas observações sobre esta derivação...'
            value={annotation}
            onChange={e => setAnnotation(e.target.value)}
            rows={3}
          />
        </Box>
      </Paper>
    </Modal>
  )
}
